// ===========================================
// Teacher Time-Off shared presentation helpers
// ===========================================
// Used by the teacher dashboard TimeOffCard and the admin TeacherTimeOffPanel.

import { TeacherUnavailabilityPeriod, TeacherUnavailabilityStatus } from '../api/users.api';
import { formatUTCDate, termWeekAnchor } from './dateUtils';

export const TIME_OFF_STATUS_CHIP: Record<
  TeacherUnavailabilityStatus,
  { label: string; bgcolor: string; color: string }
> = {
  PENDING: { label: 'Pending', bgcolor: '#FFE066', color: '#8a6d00' },
  APPROVED: { label: 'Approved', bgcolor: '#c5ebe2', color: '#2e7d64' },
  DECLINED: { label: 'Declined', bgcolor: '#ffd4cc', color: '#c05a44' },
  CANCELLED: { label: 'Cancelled', bgcolor: '#eeeeee', color: '#757575' },
};

/** "3 Aug 2026 – 7 Aug 2026", collapsing single-day ranges. */
export function timeOffRangeLabel(period: Pick<TeacherUnavailabilityPeriod, 'startDate' | 'endDate'>): string {
  const start = formatUTCDate(period.startDate);
  const end = formatUTCDate(period.endDate);
  return start === end ? start : `${start} – ${end}`;
}

const DAY_MS = 86400000;

/** UTC-midnight epoch ms for a 'YYYY-MM-DD…' date string (@db.Date ISO). */
function utcDayMs(dateStr: string): number {
  return Date.parse(`${dateStr.slice(0, 10)}T00:00:00Z`);
}

export interface TermForWeeks {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

/**
 * Term-week labels a time-off range covers — one label per overlapped term,
 * e.g. ['Term 3 2026 · Wks 2–4']. Week numbers use the same math as the
 * booking surfaces: Mon–Sun weeks anchored to the Monday of the term-start
 * week (termWeekAnchor), so weeks never straddle Monday even for a term
 * starting mid-week. Days outside every term (holidays) contribute nothing.
 */
export function timeOffTermWeekLabels(
  period: Pick<TeacherUnavailabilityPeriod, 'startDate' | 'endDate'>,
  terms: TermForWeeks[] | undefined
): string[] {
  if (!terms || terms.length === 0) return [];
  const rangeStart = utcDayMs(period.startDate);
  const rangeEnd = utcDayMs(period.endDate);
  const labels: string[] = [];
  const sorted = [...terms].sort((a, b) => utcDayMs(a.startDate) - utcDayMs(b.startDate));
  for (const term of sorted) {
    const termStart = utcDayMs(term.startDate);
    const termEnd = utcDayMs(term.endDate);
    const anchor = termWeekAnchor(term.startDate.slice(0, 10)).getTime();
    const overlapStart = Math.max(rangeStart, termStart);
    const overlapEnd = Math.min(rangeEnd, termEnd);
    if (overlapStart > overlapEnd) continue;
    const firstWeek = Math.floor((overlapStart - anchor) / (7 * DAY_MS)) + 1;
    const lastWeek = Math.floor((overlapEnd - anchor) / (7 * DAY_MS)) + 1;
    labels.push(
      firstWeek === lastWeek
        ? `${term.name} · Wk ${firstWeek}`
        : `${term.name} · Wks ${firstWeek}–${lastWeek}`
    );
  }
  return labels;
}

export function timeOffErrorMessage(error: unknown): string {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || 'Something went wrong. Please try again.';
}

/**
 * An APPROVED away range as surfaced on availability listings (e.g. the
 * Meet & Greet availability payload's `unavailablePeriods`). Dates are
 * 'YYYY-MM-DD' calendar days, both ends inclusive.
 */
export interface TeacherAwayPeriod {
  teacherId: string;
  startDate: string;
  endDate: string;
}

/**
 * Is the teacher away on the given calendar day ('YYYY-MM-DD')?
 * Lexicographic comparison is correct for zero-padded ISO date strings.
 */
export function isTeacherAwayOnDay(
  periods: TeacherAwayPeriod[] | undefined,
  teacherId: string,
  day: string
): boolean {
  if (!periods || periods.length === 0) return false;
  return periods.some(
    (p) => p.teacherId === teacherId && p.startDate <= day && p.endDate >= day
  );
}
